/**
 * Tooltip Component - Unified tooltip system with backward compatibility
 * 
 * This file provides TWO tooltip APIs:
 * 
 * 1. NEW API (Recommended for new code):
 *    - Modern tooltip with 300ms delay, glass styling, smooth animations
 *    - Usage:
 *      import Tooltip from '../components/ui/tooltip';
 *      <Tooltip content="Help text" position="top">
        <button>Hover me</button>
      </Tooltip>
 * 
 * 2. OLD API (For backward compatibility with existing code):
 *    - Simple TooltipProvider/TooltipTrigger/TooltipContent pattern
 *    - ScoreTooltip for eligibility results
 *    - Usage:
 *      import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent, ScoreTooltip } from '@/components/ui/tooltip';
 * 
 * Both APIs coexist safely. Gradually migrate old usage to new API.
 */

import React, { useState, useRef, useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle } from 'lucide-react';
const MotionDiv = motion.div;

/**
 * ============================================================================
 * BACKWARD COMPATIBILITY EXPORTS (OLD API)
 * For existing components - DO NOT REMOVE until migration is complete
 * ============================================================================
 */

// TooltipProvider component for compatibility
export const TooltipProvider = ({ children }) => {
  return <div>{children}</div>;
};

// TooltipTrigger component for compatibility
export const TooltipTrigger = ({ children, className = '' }) => {
  return <div className={className}>{children}</div>;
};

// TooltipContent component for compatibility
export const TooltipContent = ({ children, className = '' }) => {
  return <div className={className}>{children}</div>;
};

// Specialized tooltip for score/confidence explanations
export const ScoreTooltip = ({ type = 'score' }) => {
  const content = {
    score: "Weighted match against official criteria today. Based on how well your profile aligns with current grant requirements.",
    confidence: "How sure we are about this score given data completeness, rule consistency, and sensitivity. High certainty can accompany a low score when a hard rule fails."
  };

  return (
    <NewTooltip content={content[type]} position="top" delay={0}>
      <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
    </NewTooltip>
  );
};

/**
 * ============================================================================
 * NEW TOOLTIP API (Recommended for all new code)
 * Modern tooltip with delay, animations, and glass styling
 * ============================================================================
 */

/**
 * NewTooltip - Reusable tooltip with 300ms delay
 * Better UX than instant tooltips (less annoying on hover)
 * Inspired by: Figma, Linear, Vercel
 * 
 * @param {React.ReactNode} children - Trigger element
 * @param {string} content - Tooltip text
 * @param {string} position - 'top' | 'bottom' | 'left' | 'right'
 * @param {number} delay - Delay before showing (ms), default 300
 */
function NewTooltip({ 
  children, 
  content, 
  position = 'top',
  delay = 300,
  className = ''
}) {
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipId = useId();

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout to show tooltip after delay
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    // Clear timeout and hide immediately
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Escape dismisses an open tooltip (WCAG 1.4.13).
  useEffect(() => {
    if (!isVisible) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsVisible(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible]);

  // Associate the bubble with its trigger: a single element child gets aria-describedby while
  // the tooltip is shown (the id must only reference a node that exists).
  const showBubble = Boolean(isVisible && content);
  let trigger = children;
  if (showBubble && React.isValidElement(children)) {
    const existing = children.props['aria-describedby'];
    trigger = React.cloneElement(children, {
      'aria-describedby': existing ? `${existing} ${tooltipId}` : tooltipId,
    });
  }

  // Position-specific classes
  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  // Arrow position classes
  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 -mt-[1px]',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-[1px]',
    left: 'left-full top-1/2 -translate-y-1/2 -ml-[1px]',
    right: 'right-full top-1/2 -translate-y-1/2 -mr-[1px]',
  };

  // Arrow rotation
  const arrowRotation = {
    top: 'rotate-180',
    bottom: '',
    left: 'rotate-90',
    right: '-rotate-90',
  };

  return (
    <div 
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {trigger}

      <AnimatePresence>
        {showBubble && (
          <MotionDiv
            initial={{ opacity: 0, scale: 0.95, y: position === 'top' ? 5 : position === 'bottom' ? -5 : 0 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`
              absolute z-[9999] pointer-events-none
              ${positionClasses[position]}
            `}
            role="tooltip"
            id={tooltipId}
          >
            <div
              className={`
              bg-white/90 backdrop-blur-xl border border-white/20
              rounded-lg px-3 py-2 shadow-xl
              text-sm text-slate-900 font-medium
              ${className}
            `}
              style={{ maxWidth: '16rem', width: 'max-content', whiteSpace: 'normal' }}
            >
              {content}
              
              {/* Arrow */}
              <div className={`
                absolute w-2 h-2 bg-white/90 border-white/20
                ${arrowClasses[position]}
                ${arrowRotation[position]}
              `} style={{
                clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                borderTop: position === 'bottom' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
                borderLeft: position === 'right' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
              }} />
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * ============================================================================
 * EXPORTS
 * ============================================================================
 */

// Default export is the new tooltip (for new code)
export default NewTooltip;

// Named export for backward compatibility with existing imports
export const Tooltip = NewTooltip;
